import { createSelector, createFeatureSelector } from '@ngrx/store';

import { AppState, Application, AccessRule, NewRole } from '../shared/models';

export const selectApp = createFeatureSelector<AppState>('app');

export const selectExternalUser = createSelector(
  selectApp,
  (store: AppState) => store.externalUser,
);
export const selectProfileId = createSelector(selectApp, (store: AppState) => store.profileId);
export const selectExternalEntityType = createSelector(
  selectApp,
  (store: AppState) => store.externalUser?.externalEntityTypeEnum,
);

export const selectApplications = createSelector(
  selectApp,
  (store: AppState) => store.applications || [],
);
export const selectApplicationGroups = createSelector(
  selectApp,
  (store: AppState) => store.applicationGroups || [],
);
export const selectRoles = createSelector(selectApp, (store: AppState) => store.roles || []);
export const selectAccessRules = createSelector(
  selectApp,
  (store: AppState) => store.accessRules || [],
);
export const isInitialized = createSelector(selectApp, (store: AppState) => store.initialized);

export const selectSelectedApplicationGroups = createSelector(
  selectApplications,
  selectApplicationGroups,
  selectAccessRules,
  (applications, applicationGroups, accessRules) =>
    applicationGroups.filter((applicationGroup) =>
      applications
        .filter((application) => application.applicationGroupId === applicationGroup.id)
        .reduce(
          (include, application) =>
            include ||
            accessRules.filter((accessRule) => accessRule.applicationKey === application.key)
              .length > 0,
          false,
        ),
    ),
);

export const selectUnselectedApplicationGroups = createSelector(
  selectSelectedApplicationGroups,
  selectApplicationGroups,
  selectApplications,
  (selectedApplicationGroups, applicationGroups, applications) =>
    applicationGroups.filter(
      (applicationGroup) =>
        !selectedApplicationGroups.find(
          (selectedApplicationGroup) => selectedApplicationGroup.id === applicationGroup.id,
        ) &&
        applications.filter((application) => application.applicationGroupId === applicationGroup.id)
          .length > 0,
    ),
);

export const selectApplicationsForGroup = (groupId: number) =>
  createSelector(selectApplications, (applications: Application[]) =>
    applications.filter((application) => application.applicationGroupId === groupId),
  );

export const selectAccessRulesForGroup = (groupId: number) =>
  createSelector(
    selectAccessRules,
    selectApplicationsForGroup(groupId),
    (accessRules, applications) => {
      const applicationKeys = applications.map((application) => application.key);
      return accessRules.filter(
        (accessRule) => applicationKeys.indexOf(accessRule.applicationKey) !== -1,
      );
    },
  );

export const selectSelectedRole = createSelector(
  selectAccessRules,
  selectRoles,
  (accessRules: AccessRule[], roles: NewRole[], key: string) => {
    const accessRule = accessRules.find((rule) => rule.applicationKey === key);
    const accessRole = accessRule
      ? roles.find((role) => accessRule.roleKey === role.key)
      : undefined;
    return accessRole ? accessRole.key : undefined;
  },
);
